import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Loader from '../../global/loader';
import apiRequest from '../../utils/apiRequest';

interface CustomerData {
  customerId: string;
  email: string;
  name: string;
  tierLevel: string;
  shopifyId: string;
}

interface CustomerResponse {
  totalData: number;
  totalPage: number;
  customerData: Customer[];
  currentPage: number;
}

interface Customer {
  id: string;
  shopifyId: string;
  email: string;
  name: string;
  tierLevel: string;
}

const Customer = () => {
  const [loader, setLoader] = useState(true);
  const [customerData, setCustomerData] = useState<CustomerData[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [dataSize, setDataSize] = useState(100);
  const [searchQuery, setSearchQuery] = useState('');
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(
    null
  );

  const getCustomerList = async (page: number, query: string = '') => {
    setLoader(true);
    const res = await apiRequest(
      `customers/all?page=${page}&query=${query}&size=${dataSize}`
    );

    if (res.error) {
      setCustomerData([]);
    } else {
      const data: CustomerResponse = res.data;
      const customerData = data.customerData;

      const allCustomerData: CustomerData[] = customerData.map((customer) => ({
        customerId: customer.shopifyId.split('/').pop() || '',
        email: customer.email,
        name: customer.name,
        tierLevel: customer.tierLevel,
        shopifyId: customer.shopifyId,
      }));

      setCustomerData(allCustomerData);
      setTotalPages(data.totalPage);
      setCurrentPage(data.currentPage);
    }
    setLoader(false);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const newTimer = setTimeout(() => {
      getCustomerList(0, query); // Reset to the first page on new search
    }, 1000);

    setDebounceTimer(newTimer);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      getCustomerList(currentPage + 1, searchQuery);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      getCustomerList(currentPage - 1, searchQuery);
    }
  };

  useEffect(() => {
    getCustomerList(currentPage);
  }, []);

  return (
    <>
      {loader && <Loader showHide={loader} />}
      {!loader && (
        <div className='overflow-x-auto p-8'>
          <div className='grid grid-cols-2 items-center pb-6'>
            <div className='justify-self-start'>
              <h2 className='text-xl font-bold mb-2'>Customer</h2>
            </div>
            <div className='justify-self-end'>
              <input
                className='border p-2'
                placeholder='Name or Email Search... '
                style={{ width: '319px' }}
                value={searchQuery}
                onChange={handleSearch}
              />
            </div>
          </div>

          <div className='mr-0 ml-auto w-full'>
            <div className='flex justify-end py-2'>
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 0}
                className='w-[77px] text-[12px] h-[30px] bg-white border rounded-[3px] mr-[2px] hover:bg-[#f2f0f0] cursor-pointer disabled:bg-[#f2f0f0] disabled:text-[#9e9595]'
              >
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage >= totalPages - 1}
                className='w-[77px] text-[12px] bg-white h-[30px] border rounded-[3px] mr-[2px] hover:bg-[#f2f0f0] cursor-pointer disabled:bg-[#f2f0f0] disabled:text-[#9e9595]'
              >
                Next
              </button>
            </div>
          </div>
          <table className='min-w-full'>
            <thead className='text-[16px]'>
              <tr className='text-left bg-inherit'>
                <th className='py-2 px-4 border-b border-[#B3B3B3]'>
                  Name & Address
                </th>
                <th className='py-2 px-4 border-b border-[#B3B3B3]'>
                  Tier Level
                </th>
              </tr>
            </thead>
            <tbody>
              {customerData.length > 0 ? (
                customerData.map((item) => (
                  <tr key={item.customerId} className='text-left'>
                    <td className='py-2 px-3 border-b border-[#B3B3B3]'>
                      <Link
                        to={`/customer-details?id=${item.shopifyId}`}
                        className='text-blue-600 underline'
                      >
                        {item.name}
                      </Link>
                      <br />
                      {item.email}
                    </td>
                    <td className='py-2 px-4 border-b border-[#B3B3B3]'>
                      {item.tierLevel || '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={2}
                    className='text-center py-4 text-gray-500 border-b border-[#B3B3B3]'
                  >
                    No customers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default Customer;
